import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { NgModule, isDevMode, APP_INITIALIZER } from "@angular/core";
import { AgGridModule } from "@ag-grid-community/angular";
import { CommonModule } from "@angular/common";
import { AppRoutingModule } from "./app-routing.module";
import { AppComponent } from "./app.component";

import { DashboardModule } from "./components/dashboard/dashboard.module";
import { FirmaModule } from "./components/firmas/firma.module";
import { SharedModule } from "./shared/shared.module";
import { MenusModule } from "./components/menus/menus.module";
import { UsersModule } from "./components/users/users.module";
import { SettingModule } from "./components/setting/setting.module";
import { AuthModule } from "./components/auth/auth.module";
import { MonacoEditorModule } from "ngx-monaco-editor-v2";

import { AuthGuard } from "./guards/auth.guard";
import { HTTP_INTERCEPTORS, HttpClientModule } from "@angular/common/http";
import { AuthInterceptor } from "./core/interceptors/auth-interceptor.interceptor";

import { NgxDropzoneModule } from "ngx-dropzone";
import { DropzoneModule } from "ngx-dropzone-wrapper";
import { PdfViewerModule } from "ng2-pdf-viewer";
import { ToastrModule } from "ngx-toastr";
import { HttpErrorInterceptor } from "./core/interceptors/http-error.interceptor";
import { SocketIoConfig, SocketIoModule } from "ngx-socket-io";
import { environment } from "src/environments/environment";
import { ServiceWorkerModule } from "@angular/service-worker";
import { HttpLoadingInterceptor } from "./core/interceptors/http-loading.interceptor";
import { GroupsModule } from "./components/groups/groups.module";
import { NZ_I18N, es_ES } from "ng-zorro-antd/i18n";
import { NzButtonModule } from "ng-zorro-antd/button";
import { NzTableModule } from "ng-zorro-antd/table";
import { NgSelectModule } from "@ng-select/ng-select";
import { PerfilModule } from "./components/perfil/perfil.module";
import { MustChangePasswordGuard } from "./guards/must-chang-password.guard";
import { InstitucionModule } from "./components/institucion/institucion.module";
import { BrandingService } from "./core/services/branding.service";
import { ParametrosModule } from "./components/parametros/parametros.module";
import { AccesosModule } from "./components/accesos/accesos.module";
import { UltimosLotesCompletadosModule } from "./components/ultimos-lotes-completados/ultimos-lotes-completados.module";
import { FirmasPendientesAvanceModule } from "./components/firmas-pendientes-avance/firmas-pendientes-avance.module";
import { DocumentosPorFirmarModule } from "./components/documentos-por-firmar/documentos-por-firmar.module";
import { SignatureSizeMtoModule } from "./components/signature-size-mto/signature-size-mto.module";
import { IntegracionesModule } from "./components/integraciones/integraciones.module";
import { VisorWrapperModule } from "./components/visor/visor-wrapper.module";

export function brandingInitializer(brandingService: BrandingService) {
  // Debe devolver una función que retorne Promise<void>
  return () => brandingService.load();
}

const config: SocketIoConfig = {
  url: `${environment.backendHost}:${environment.backendPort}`,
  options: {
    path: "/socket.io/",
    transports: ["websocket"],
  },
};

@NgModule({
  declarations: [AppComponent],
  imports: [
    HttpClientModule,
    BrowserAnimationsModule,
    BrowserModule.withServerTransition({ appId: "serverApp" }),
    AppRoutingModule,
    DashboardModule,
    SettingModule,
    AuthModule,
    SharedModule,
    FirmaModule,
    MenusModule,
    UsersModule,
    GroupsModule,
    AgGridModule,
    NgxDropzoneModule,
    DropzoneModule,
    PdfViewerModule,
    NzButtonModule,
    NzTableModule,
    NgSelectModule,
    CommonModule,
    PerfilModule,
    ParametrosModule,
    AccesosModule,
    SignatureSizeMtoModule,
    InstitucionModule,
    UltimosLotesCompletadosModule,
    FirmasPendientesAvanceModule,
    DocumentosPorFirmarModule,
    VisorWrapperModule,
    IntegracionesModule,
    MonacoEditorModule.forRoot(),
    ToastrModule.forRoot({
      // <-- Configura ToastrModule aquí
      timeOut: 5000, // Duración por defecto del toast en ms
      positionClass: "toast-top-right", // Posición en la pantalla
      preventDuplicates: true, // Evita toasts duplicados
      // Puedes añadir más opciones globales aquí
    }),
    SocketIoModule.forRoot(config),
    ServiceWorkerModule.register("custom-service-worker.js", {
      enabled: !isDevMode(),
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: "registerImmediately",
    }),
  ],
  providers: [
    AuthGuard,
    MustChangePasswordGuard,
    BrandingService,
    {
      provide: APP_INITIALIZER,
      useFactory: brandingInitializer,
      deps: [BrandingService],
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpErrorInterceptor,
      multi: true, // Muy importante: permite tener múltiples interceptores
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true, // ✅ importante
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpLoadingInterceptor,
      multi: true,
    },
    { provide: NZ_I18N, useValue: es_ES },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
